import type * as api from '@gitbook/api-client';
import { allTranslationLanguages, translationLanguageDescriptors } from '@gitbook/app-translations';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteDef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Emoji } from '@gitbook/spine-react';

import type { GlossaryStateActions } from './state';

type GlossaryLanguagesButtonProps = {
    /** The languages currently displayed. */
    displayedLanguages: api.TranslationLanguage[];
    /** The actions to perform on the glossary. */
    actions: GlossaryStateActions;
    /** The mode of the button. */
    mode: 'select' | 'add';
};

export function GlossaryLanguagesButton(props: GlossaryLanguagesButtonProps) {
    const { displayedLanguages, actions, mode = 'select' } = props;

    return (
        <PaletteButton
            label={
                mode === 'select'
                    ? 'Select languages to display'
                    : 'Select more languages to display'
            }
            iconOnly
            leadingIcon={mode === 'select' ? Icon.Filter : Icon.Plus}
            kind="blank"
            palette={() =>
                createLanguagesPalette({
                    displayedLanguages,
                    actions,
                    mode,
                })
            }
        />
    );
}

function createLanguagesPalette({
    displayedLanguages,
    actions,
    mode,
}: GlossaryLanguagesButtonProps): PaletteDef {
    return createPalette({
        search: {
            placeholder: 'Search languages',
        },
        groups: [
            {
                key: 'languages',
                commands: (mode === 'select'
                    ? allTranslationLanguages
                    : allTranslationLanguages.filter(
                          (language) => !displayedLanguages.includes(language)
                      )
                ).map((language) => {
                    const selected = displayedLanguages.includes(language);
                    return {
                        type: PaletteCommandType.CHECKBOX,
                        key: language,
                        title: translationLanguageDescriptors[language].label,
                        icon: <Emoji code={translationLanguageDescriptors[language].emoji} />,
                        selected,
                        trigger: () => {
                            const newDisplayedLanguages = allTranslationLanguages.filter(
                                (displayLanguage) => {
                                    if (displayLanguage === language) {
                                        return !selected;
                                    }

                                    return displayedLanguages.includes(displayLanguage);
                                }
                            );

                            actions.setDisplayedLanguages(newDisplayedLanguages);

                            return createLanguagesPalette({
                                displayedLanguages: newDisplayedLanguages,
                                actions,
                                mode,
                            });
                        },
                    };
                }),
            },
        ],
    });
}
